// The password rules, in one place, stated the same way the UI and the server both need them.
//
// ⚠ THIS NUMBER IS ALSO SET IN THE SUPABASE PROJECT, and the two must agree. If the client lets someone type a
// 6-character password the server will refuse it — and the refusal arrives as a raw API string in the middle of
// a sign-up flow, which is the worst possible moment to be told a rule that could have been shown up front.
// `npm run check:auth-config` reads the live project and fails if the server's minimum is not this number.
export const PASSWORD_MIN_LENGTH = 8;
/**
 * The second rule (owner, 2026-10-02, the SAME as Campaign Compendium — the two apps share one Supabase project and one
 * set of accounts): at least one letter, one number and one symbol. The server can only say "letters and digits", and so does
 * the server (password_required_characters = letters:digits). check:auth-config asserts the server's set.
 */
export const PASSWORD_LETTER = /[A-Za-z]/;
export const PASSWORD_DIGIT = /[0-9]/;
export const PASSWORD_RULE = `at least ${PASSWORD_MIN_LENGTH} characters, with a letter and a number`;

/** Where the email link lands for a brand-new account, and for a reset. Separate paths on purpose — see auth.tsx. */
export const WELCOME_PATH = "/welcome";
export const RESET_PATH = "/reset";

/** Key for the one-line confirmation that has to survive the trip back to the sign-in screen. */
export const TOAST_KEY = "vttmaps.auth.notice";

/**
 * What is wrong with this pair of passwords, in words a person can act on, or null when they are fine.
 *
 * ⚠ ORDER MATTERS. "Too short" is reported before "they don't match": someone still typing the confirmation
 * has not made a mistake yet, and telling them so on every keystroke is how a form feels hostile.
 */
export function passwordProblem(password: string, confirm: string): string | null {
  if (!password) return "Choose a password.";
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `That is ${password.length} character${password.length === 1 ? "" : "s"} — passwords need at least `
         + `${PASSWORD_MIN_LENGTH}.`;
  }
  if (!PASSWORD_LETTER.test(password) || !PASSWORD_DIGIT.test(password)) return "Passwords need at least one letter and one number.";
  if (!confirm) return "Type the password a second time to confirm it.";
  if (password !== confirm) return "The two passwords are not the same.";
  return null;
}

/** Whether the form can be submitted at all. Kept beside the message so a button and its error never disagree. */
export const passwordsReady = (password: string, confirm: string): boolean =>
  passwordProblem(password, confirm) === null;

/**
 * Turn an auth error into something worth reading.
 *
 * ⚠ SUPABASE'S STRINGS ARE WRITTEN FOR DEVELOPERS. "Invalid login credentials" is accurate and tells a person
 * nothing about what to do next, and "User already registered" appears at the exact moment someone is trying
 * to make an account and needs to be pointed at sign-in instead.
 */
export function friendlyAuthError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes("invalid login credentials")) {
    return "That email and password don't match an account. Check them, or reset your password below.";
  }
  if (m.includes("email not confirmed")) {
    return "This account hasn't been finished yet — use the link in your sign-up email to choose a password.";
  }
  if (m.includes("user already registered") || m.includes("already been registered")) {
    return "That address already has an account. Sign in instead, or reset the password.";
  }
  if (m.includes("for security purposes") || m.includes("rate limit") || m.includes("too many")) {
    return "That was a lot of attempts in a short time. Wait a minute and try again.";
  }
  if (m.includes("same as the old password") || m.includes("should be different")) {
    return "That is the password you already had. Choose a different one.";
  }
  if (m.includes("expired") || m.includes("invalid") || m.includes("not found")) {
    return "That link has expired or has already been used. Ask for a new one below.";
  }
  return raw;
}
